import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import config from "../config";
import AdminShell, { eur, d8 } from "./AdminShell";

const API = config.API_BASE_URL;

const STATUS_CHIP = { draft: "trial", issued: "active", failed: "inactive", dismissed: "" };

export default function AdminInvoices() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState({ companyId: "", description: "", amountEur: "", period: "" });

  const load = useCallback(() => {
    axios.get(`${API}/admin/invoices`).then((r) => setData(r.data)).catch(() => {});
    axios.get(`${API}/admin/companies`).then((r) => setCompanies(r.data.companies)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <AdminShell><div className="ad-empty">…</div></AdminShell>;

  if (!data.enabled || !data.connected)
    return (
      <AdminShell>
        <div className="ad-card">
          <h3>{t("Invoices")}</h3>
          <div className="ad-banner warn">{t("Invoicing is not connected yet — waiting for the Elorus account & API key.")}</div>
        </div>
      </AdminShell>
    );

  const act = async (fn, okMsg) => {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      setMsg(okMsg);
      load();
    } catch (e) {
      setMsg(e?.response?.data?.error || t("Something went wrong — please try again."));
    } finally {
      setBusy(false);
    }
  };

  const createDraft = () =>
    act(async () => {
      await axios.post(`${API}/admin/invoices`, {
        companyId: Number(form.companyId),
        description: form.description,
        amountEur: Number(form.amountEur),
        period: form.period || null,
      });
      setForm({ companyId: "", description: "", amountEur: "", period: "" });
    }, t("Draft created."));

  const importPayments = () =>
    act(async () => { await axios.post(`${API}/admin/invoices/import-payments`); }, t("Payments imported as drafts."));

  const approve = (q) => {
    const warn = data.forceDraft
      ? t("(dev: it will be created as a DRAFT in Elorus — nothing goes to myDATA)")
      : t("The invoice will be ISSUED, emailed to the customer and transmitted to myDATA.");
    if (!window.confirm(`${t("Approve invoice")}: ${q.companyName} — ${eur(q.amountEur)};\n${warn}`)) return;
    act(async () => { await axios.post(`${API}/admin/invoices/${q.id}/approve`); }, t("Invoice issued via Elorus."));
  };

  const dismiss = (q) => {
    if (!window.confirm(t("Dismiss this draft?"))) return;
    act(async () => { await axios.post(`${API}/admin/invoices/${q.id}/dismiss`); }, t("Dismissed."));
  };

  const pending = data.queue.filter((q) => q.status === "draft" || q.status === "failed");
  const done = data.queue.filter((q) => q.status === "issued" || q.status === "dismissed");

  return (
    <AdminShell>
      {data.forceDraft && (
        <div className="ad-banner warn">
          🧪 {t("Safe mode: approvals create DRAFT invoices in Elorus (no myDATA transmission). Disable ELORUS_FORCE_DRAFT in production.")}
        </div>
      )}
      {msg && <div className="ad-banner">{msg}</div>}

      <div className="ad-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", marginBottom: 14 }}>
        <div className="ad-card">
          <h3>{t("New draft invoice")}</h3>
          <div className="ad-form-row">
            <label>{t("Customer")}</label>
            <select value={form.companyId} onChange={(e) => setForm({ ...form, companyId: e.target.value })}>
              <option value="">—</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="ad-form-row">
            <label>{t("Description")}</label>
            <input type="text" autoCapitalize="off" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder={t("EstiaSync subscription")} />
          </div>
          <div className="ad-form-row">
            <label>{t("Amount (incl. VAT)")}</label>
            <input type="number" min="1" step="0.01" value={form.amountEur}
              onChange={(e) => setForm({ ...form, amountEur: e.target.value })} placeholder="97.96" />
          </div>
          <div className="ad-form-row">
            <label>{t("Period")}</label>
            <input type="text" autoCapitalize="off" value={form.period}
              onChange={(e) => setForm({ ...form, period: e.target.value })} placeholder="2026-09" />
          </div>
          <button className="ad-btn primary" disabled={busy || !form.companyId || !form.description || !form.amountEur}
            onClick={createDraft}>
            {t("Create draft")}
          </button>
        </div>

        <div className="ad-card">
          <h3>{t("Import from payments")}</h3>
          <p className="ad-hint">
            {t("Paid Viva transactions without an invoice")}: <strong>{data.importablePayments}</strong>
          </p>
          <button className="ad-btn" disabled={busy || !data.importablePayments} onClick={importPayments}>
            {t("Import as drafts")}
          </button>
        </div>
      </div>

      <h3 style={{ fontSize: 15, margin: "8px 0" }}>{t("Pending approval")} ({pending.length})</h3>
      {pending.length === 0 ? (
        <div className="ad-empty">{t("No pending invoices.")}</div>
      ) : (
        <div className="ad-table-scroll" style={{ marginBottom: 16 }}>
          <table className="ad-table">
            <thead>
              <tr>
                <th>ID</th><th>{t("Customer")}</th><th>{t("Description")}</th><th>{t("Amount")}</th>
                <th>{t("Status")}</th><th></th>
              </tr>
            </thead>
            <tbody>
              {pending.map((q) => (
                <tr key={q.id}>
                  <td>{q.id}</td>
                  <td>
                    {q.companyName}
                    {q.dataWarnings.includes("taxid") && <div className="ad-chip grace">⚠ {t("missing Tax ID")}</div>}
                    {q.dataWarnings.includes("email") && <div className="ad-chip grace">⚠ {t("missing email")}</div>}
                  </td>
                  <td>{q.description}{q.period ? ` (${q.period})` : ""}<div className="ad-hint">{q.source} · {d8(q.created_at)}</div></td>
                  <td><strong>{eur(q.amountEur)}</strong></td>
                  <td>
                    <span className={`ad-chip ${STATUS_CHIP[q.status]}`}>{t(q.status)}</span>
                    {q.elorus_error && <div className="ad-hint" style={{ color: "#b91c1c", maxWidth: 260 }}>{q.elorus_error}</div>}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="ad-btn primary" disabled={busy} onClick={() => approve(q)}>✅ {t("Approve & issue")}</button>{" "}
                    <button className="ad-btn danger" disabled={busy} onClick={() => dismiss(q)}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 style={{ fontSize: 15, margin: "8px 0" }}>{t("History")} ({done.length})</h3>
      {done.length === 0 ? (
        <div className="ad-empty">—</div>
      ) : (
        <div className="ad-table-scroll">
          <table className="ad-table">
            <thead>
              <tr>
                <th>ID</th><th>{t("Customer")}</th><th>{t("Description")}</th><th>{t("Amount")}</th>
                <th>{t("Status")}</th><th>Elorus</th>
              </tr>
            </thead>
            <tbody>
              {done.map((q) => (
                <tr key={q.id}>
                  <td>{q.id}</td>
                  <td>{q.companyName}</td>
                  <td>{q.description}{q.period ? ` (${q.period})` : ""}</td>
                  <td>{eur(q.amountEur)}</td>
                  <td>
                    <span className={`ad-chip ${STATUS_CHIP[q.status]}`}>{t(q.status)}</span>
                    {!!q.elorus_draft && <span className="ad-chip trial">{t("Elorus draft")}</span>}
                    {!!q.email_sent && <span className="ad-chip ok">📧</span>}
                  </td>
                  <td className="ad-hint">{q.elorus_invoice_id || "—"}{q.issued_at ? ` · ${d8(q.issued_at)}` : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminShell>
  );
}
